import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, ArrowLeft, Upload, Loader, Plus, Trash2 } from 'lucide-react';
import { landingService } from '../services/landingService';
import { getStorageUrl } from '../utils/storageUrl';

const LandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [club, setClub] = useState(null);
  const [landingConfig, setLandingConfig] = useState(null);
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [children, setChildren] = useState([]); // Cada hijo: { name, surname, birth_date, shirt_size, rate_id }

  // Datos del formulario
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    document_type: 'dni',
    document_number: '',
    gender: '',
    gender_custom: '',
    shirt_size: '',
    birth_date: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    province: '',
    country: 'España',
    disciplines: [],
    comments: '',
    photo: null,
    rate_id: '',
    year: new Date().getFullYear()
  });

  useEffect(() => {
    loadLandingData();
  }, [slug]);

  const loadLandingData = async () => {
    try {
      setLoading(true);
      const data = await landingService.getLanding(slug);
      setClub(data.club);
      setLandingConfig(data.landing_config);
      setRates(data.rates || []);
    } catch (error) {
      console.error('Error cargando landing:', error);
      alert('Error al cargar la página de inscripción. El club no existe o está inactivo.');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      if (name === 'photo') {
        const photoFile = files[0];
        if (photoFile) {
          setFormData(prev => ({ ...prev, photo: photoFile }));
          const reader = new FileReader();
          reader.onloadend = () => {
            // No guardamos preview de foto aquí, solo del archivo adjunto
          };
          reader.readAsDataURL(photoFile);
        }
      } else if (name === 'file') {
        const fileToUpload = files[0];
        if (fileToUpload) {
          setFile(fileToUpload);
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreview(reader.result);
          };
          reader.readAsDataURL(fileToUpload);
        }
      }
    } else if (type === 'checkbox') {
      const discipline = value;
      setFormData(prev => {
        const currentDisciplines = prev.disciplines || [];
        if (checked) {
          return { ...prev, disciplines: [...currentDisciplines, discipline] };
        } else {
          return { ...prev, disciplines: currentDisciplines.filter(d => d !== discipline) };
        }
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Limpiar errores
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRateSelect = (rate) => {
    setSelectedRate(rate);
    setFormData(prev => ({ ...prev, rate_id: rate.id }));
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      // Paso 1 no requiere validación
      return true;
    }

    if (stepNumber === 2) {
      if (!formData.rate_id) {
        newErrors.rate_id = 'Debes seleccionar una tarifa';
      }
    }

    if (stepNumber === 5) {
      if (!file) {
        newErrors.file = 'Debes subir el justificante de pago';
      }
    }

    if (stepNumber === 3) {
      if (!formData.name.trim()) {
        newErrors.name = 'El nombre es obligatorio';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'El email es obligatorio';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'El email no es válido';
      }
      if (formData.gender === 'otro' && !formData.gender_custom.trim()) {
        newErrors.gender_custom = 'Debes especificar el género personalizado';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleAddChild = () => {
    setChildren([...children, { name: '', surname: '', birth_date: '', shirt_size: '', rate_id: '' }]);
  };

  const handleRemoveChild = (index) => {
    setChildren(children.filter((_, i) => i !== index));
  };

  const handleChildChange = (index, field, value) => {
    const updatedChildren = [...children];
    updatedChildren[index][field] = value;
    setChildren(updatedChildren);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(step)) {
      return;
    }

    setSubmitting(true);
    try {
      const submitFormData = new FormData();

      // Agregar todos los campos del formulario
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (key === 'photo' && value instanceof File) {
          submitFormData.append('photo', value);
        } else if (key === 'disciplines' && Array.isArray(value)) {
          value.forEach(discipline => {
            submitFormData.append('disciplines[]', discipline);
          });
        } else if (value !== null && value !== undefined && value !== '') {
          submitFormData.append(key, value);
        }
      });

      // Agregar archivo adjunto si existe
      if (file) {
        submitFormData.append('file', file);
      }

      // Agregar hijos si existen
      if (children.length > 0) {
        children.forEach((child, index) => {
          submitFormData.append(`children[${index}][name]`, child.name);
          if (child.surname) {
            submitFormData.append(`children[${index}][surname]`, child.surname);
          }
          if (child.birth_date) {
            submitFormData.append(`children[${index}][birth_date]`, child.birth_date);
          }
          if (child.shirt_size) {
            submitFormData.append(`children[${index}][shirt_size]`, child.shirt_size);
          }
          submitFormData.append(`children[${index}][rate_id]`, child.rate_id);
        });
      }

      await landingService.subscribe(slug, submitFormData);
      setSuccess(true);
    } catch (error) {
      console.error('Error al enviar inscripción:', error);
      const errorMessage = error.response?.data?.message || 'Error al procesar la inscripción. Inténtalo de nuevo.';
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  const handleResetForm = () => {
    setStep(1);
    setSuccess(false);
    setSelectedRate(null);
    setFile(null);
    setFilePreview(null);
    setChildren([]);
    setErrors({});
    setFormData({
      name: '',
      surname: '',
      document_type: 'dni',
      document_number: '',
      gender: '',
      gender_custom: '',
      shirt_size: '',
      birth_date: '',
      email: '',
      phone: '',
      address: '',
      postal_code: '',
      city: '',
      province: '',
      country: 'España',
      disciplines: [],
      comments: '',
      photo: null,
      rate_id: '',
      year: new Date().getFullYear()
    });
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Inscripción exitosa!</h2>
          <p className="text-gray-600 mb-6">
            Tu inscripción ha sido procesada correctamente. Recibirás un email de confirmación.
          </p>
          <button
            onClick={handleResetForm}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Realizar otra inscripción
          </button>
        </div>
      </div>
    );
  }

  const SPANISH_PROVINCES = [
    'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 'Badajoz', 'Barcelona',
    'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ciudad Real', 'Córdoba', 'La Coruña',
    'Cuenca', 'Gerona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva', 'Huesca', 'Jaén',
    'León', 'Lérida', 'Lugo', 'Madrid', 'Málaga', 'Murcia', 'Navarra', 'Orense',
    'Palencia', 'Las Palmas', 'Pontevedra', 'La Rioja', 'Salamanca', 'Santa Cruz de Tenerife',
    'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid',
    'Vizcaya', 'Zamora', 'Zaragoza'
  ];

  const DISCIPLINES = [
    'Senderismo', 'Escalada', 'Alpinismo', 'Vías ferratas', 'Descenso de barrancos',
    'Carreras por montaña', 'Marcha nórdica', 'Esquí de travesía', 'BTT', 'Otro'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con logo del club */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {club?.logo && (
              <img 
              src={getStorageUrl(club.logo)}
              alt={club.name}
              className="h-16 mx-auto object-contain"
            />
          )}
          <h1 className="text-2xl font-bold text-center text-gray-900 mt-4">{club?.name}</h1>
        </div>
      </div>

      {/* Indicador de pasos */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <React.Fragment key={s}>
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > s ? <CheckCircle size={20} /> : s}
                </div>
                <span className={`ml-2 font-medium ${step >= s ? 'text-blue-600' : 'text-gray-600'}`}>
                  {s === 1 ? 'Información' : s === 2 ? 'Tarifa' : s === 3 ? 'Datos' : s === 4 ? 'Hijos' : 'Resumen'}
                </span>
              </div>
              {s < 5 && <div className={`w-16 h-1 mx-4 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Contenido de los pasos */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Paso 1: Información del club */}
          {step === 1 && (
            <div>
              {landingConfig?.step1_content ? (
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: landingConfig.step1_content }}
                />
              ) : (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Bienvenido a {club?.name}</h2>
                  <p className="text-gray-600 mb-4">
                    Únete a nuestro club y disfruta de todas nuestras actividades.
                  </p>
                </div>
              )}
              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleNext}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  Continuar
                  <ArrowRight size={20} className="ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Paso 2: Selección de tarifa */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Selecciona tu tarifa</h2>
              
              {errors.rate_id && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800">{errors.rate_id}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {rates.map((rate) => (
                  <div
                    key={rate.id}
                    onClick={() => handleRateSelect(rate)}
                    className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                      selectedRate?.id === rate.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{rate.name}</h3>
                      {rate.is_popular && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                          Popular
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4">{rate.description}</p>
                    <div className="text-2xl font-bold text-blue-600 mb-4">
                      {rate.price} {rate.currency} / {rate.period}
                    </div>
                    {rate.features && rate.features.length > 0 && (
                      <ul className="space-y-2">
                        {rate.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>


              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Atrás
                </button>
                <button
                  onClick={handleNext}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  Continuar
                  <ArrowRight size={20} className="ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Paso 3: Datos del socio */}
          {step === 3 && (
            <form onSubmit={handleSubmit}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Datos personales</h2>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Nombre y Apellidos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apellidos
                    </label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Documento */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Documento
                    </label>
                    <select
                      name="document_type"
                      value={formData.document_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="dni">DNI</option>
                      <option value="pasaporte">Pasaporte</option>
                      <option value="nie">NIE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Documento
                    </label>
                    <input
                      type="text"
                      name="document_number"
                      value={formData.document_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Género */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Género
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="mujer"
                        checked={formData.gender === 'mujer'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Mujer
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="hombre"
                        checked={formData.gender === 'hombre'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Hombre
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="otro"
                        checked={formData.gender === 'otro'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Otro
                    </label>
                  </div>
                  {formData.gender === 'otro' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        name="gender_custom"
                        value={formData.gender_custom}
                        onChange={handleInputChange}
                        placeholder="Especificar género personalizado"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.gender_custom ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.gender_custom && (
                        <p className="mt-1 text-sm text-red-600">{errors.gender_custom}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Talla y Fecha de nacimiento */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Talla de Camiseta
                    </label>
                    <select
                      name="shirt_size"
                      value={formData.shirt_size}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecciona una talla</option>
                      <option value="xs">XS</option>
                      <option value="s">S</option>
                      <option value="m">M</option>
                      <option value="l">L</option>
                      <option value="xl">XL</option>
                      <option value="xxl">XXL</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Nacimiento
                    </label>
                    <input
                      type="date"
                      name="birth_date"
                      value={formData.birth_date}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Email y Teléfono */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Dirección */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código Postal
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Población
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provincia
                    </label>
                    <select
                      name="province"
                      value={formData.province}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecciona una provincia</option>
                      {SPANISH_PROVINCES.map(province => (
                        <option key={province} value={province}>{province}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* País */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    País
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Foto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto
                  </label>
                  <input
                    type="file"
                    name="photo"
                    accept="image/*"
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                {/* Disciplinas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Disciplinas que Practicas
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {DISCIPLINES.map(discipline => (
                      <label key={discipline} className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          value={discipline}
                          checked={formData.disciplines?.includes(discipline)}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{discipline}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Comentarios */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comentarios
                  </label>
                  <textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handleBack}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  Continuar
                  <ArrowRight size={20} className="ml-2" />
                </button>
              </div>
            </form>
          )}

          {/* Paso 4: Hijos (Opcional) */}
          {step === 4 && (
            <form onSubmit={handleSubmit}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Hijos (Opcional)</h2>
              <p className="text-gray-600 mb-6">
                Si deseas inscribir a tus hijos, puedes añadirlos aquí. Cada hijo debe tener una tarifa seleccionada.
              </p>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Botón para añadir hijo */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddChild}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={16} className="mr-2" />
                    Añadir Hijo
                  </button>
                </div>

                {/* Lista de hijos */}
                {children.length > 0 && (
                  <div className="space-y-6">
                    {children.map((child, index) => (
                      <div key={index} className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-gray-900">Hijo {index + 1}</h3>
                          <button
                            type="button"
                            onClick={() => handleRemoveChild(index)}
                            className="text-red-600 hover:text-red-800 flex items-center"
                          >
                            <Trash2 size={18} className="mr-1" />
                            Eliminar
                          </button>
                        </div>

                        {/* Nombre y Apellidos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nombre *
                            </label>
                            <input
                              type="text"
                              value={child.name}
                              onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                errors[`child_${index}_name`] ? 'border-red-300' : 'border-gray-300'
                              }`}
                              required
                            />
                            {errors[`child_${index}_name`] && (
                              <p className="mt-1 text-sm text-red-600">{errors[`child_${index}_name`]}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Apellidos
                            </label>
                            <input
                              type="text"
                              value={child.surname}
                              onChange={(e) => handleChildChange(index, 'surname', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Fecha de nacimiento y Talla de camiseta */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Fecha de Nacimiento
                            </label>
                            <input
                              type="date"
                              value={child.birth_date}
                              onChange={(e) => handleChildChange(index, 'birth_date', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Talla de Camiseta
                            </label>
                            <select
                              value={child.shirt_size}
                              onChange={(e) => handleChildChange(index, 'shirt_size', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Selecciona una talla</option>
                              <option value="xs">XS</option>
                              <option value="s">S</option>
                              <option value="m">M</option>
                              <option value="l">L</option>
                              <option value="xl">XL</option>
                              <option value="xxl">XXL</option>
                            </select>
                          </div>
                        </div>

                        {/* Selección de tarifa - igual que en paso 2 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-4">
                            Selecciona la tarifa para este hijo *
                          </label>
                          {errors[`child_${index}_rate`] && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                              <p className="text-red-800">{errors[`child_${index}_rate`]}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {rates.map((rate) => (
                              <div
                                key={rate.id}
                                onClick={() => handleChildChange(index, 'rate_id', rate.id)}
                                className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                                  child.rate_id === rate.id
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <h4 className="text-xl font-bold text-gray-900">{rate.name}</h4>
                                  {rate.is_popular && (
                                    <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                                      Popular
                                    </span>
                                  )}
                                </div>
                                <p className="text-gray-600 mb-4">{rate.description}</p>
                                <div className="text-2xl font-bold text-blue-600 mb-4">
                                  {rate.price} {rate.currency} / {rate.period}
                                </div>
                                {rate.features && rate.features.length > 0 && (
                                  <ul className="space-y-2">
                                    {rate.features.map((feature, featureIndex) => (
                                      <li key={featureIndex} className="flex items-start">
                                        <CheckCircle size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                                        <span className="text-sm text-gray-700">{feature}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {children.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="text-gray-600 mb-4">
                      No has añadido ningún hijo. Puedes continuar sin añadir hijos o hacer clic en "Añadir Hijo" para comenzar.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handleBack}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  Continuar
                  <ArrowRight size={20} className="ml-2" />
                </button>
              </div>
            </form>
          )}

          {/* Paso 5: Resumen y Justificante de Pago */}
          {step === 5 && (
            <form onSubmit={handleSubmit}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Resumen y Justificante de Pago</h2>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Resumen de Tarifas */}
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen de Tarifas Seleccionadas</h3>
                  
                  {/* Tarifa del socio principal */}
                  {selectedRate && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">Socio Principal</h4>
                          <p className="text-sm text-gray-600 mt-1">{selectedRate.name}</p>
                          {selectedRate.description && (
                            <p className="text-sm text-gray-500 mt-1">{selectedRate.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-600">
                            {selectedRate.price} {selectedRate.currency} / {selectedRate.period}
                          </p>
                        </div>
                      </div>
                      {selectedRate.features && selectedRate.features.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {selectedRate.features.map((feature, index) => (
                            <li key={index} className="flex items-start text-sm text-gray-700">
                              <CheckCircle size={14} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Tarifas de hijos */}
                  {children.length > 0 && (
                    <div className="space-y-4">
                      {children.map((child, index) => {
                        const childRate = rates.find(r => r.id === child.rate_id);
                        if (!childRate) return null;
                        
                        return (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {child.name} {child.surname || ''} (Hijo)
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">{childRate.name}</p>
                                {childRate.description && (
                                  <p className="text-sm text-gray-500 mt-1">{childRate.description}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-blue-600">
                                  {childRate.price} {childRate.currency} / {childRate.period}
                                </p>
                              </div>
                            </div>
                            {childRate.features && childRate.features.length > 0 && (
                              <ul className="mt-3 space-y-1">
                                {childRate.features.map((feature, featureIndex) => (
                                  <li key={featureIndex} className="flex items-start text-sm text-gray-700">
                                    <CheckCircle size={14} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Total */}
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Total de Tarifas:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {1 + children.length} {children.length === 0 ? 'tarifa' : 'tarifas'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Subida de Justificante de Pago */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Upload size={16} className="inline mr-1" />
                    Justificante de Pago *
                  </label>
                  <p className="text-sm text-gray-600 mb-4">
                    Por favor, sube el justificante de pago para todas las tarifas contratadas.
                  </p>
                  <input
                    type="file"
                    name="file"
                    onChange={handleInputChange}
                    accept="image/*,.pdf,.doc,.docx"
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${
                      errors.file ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.file && (
                    <p className="mt-1 text-sm text-red-600">{errors.file}</p>
                  )}
                  {filePreview && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 mb-2">Vista previa:</p>
                      {file?.type?.startsWith('image/') ? (
                        <img src={filePreview} alt="Preview" className="max-w-xs rounded-lg" />
                      ) : (
                        <p className="text-sm text-gray-600">{file?.name}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={handleBack}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Atrás
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      Finalizar Inscripción
                      <CheckCircle size={20} className="ml-2" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
